import { defineComponent as _defineComponent } from 'vue'
import { AuthorityType, Client, ClientPositionUsageReport } from 'ah-api-gateways';
import { BModal } from 'bootstrap-vue';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpError, GenericErrorCodes } from 'ah-requests';
import { DEFAULT_INACTIVITY_TIMEOUT } from 'ah-common-lib/src/constants/time';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { computed, onBeforeUnmount, onBeforeMount, reactive, ref, onMounted } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useTradeState } from '../..';

import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'PositionLimits',
  props: {
    instrumentHeader: { default: 'Instrument' },
    limitHeader: { default: 'Max. Open Positions Notional Value' },
    inUseHeader: { default: 'Current Usage' },
    remainingHeader: { default: 'Remaining Trade Limit' },
    spotTitle: { default: 'Spot' },
    forwardTitle: { default: 'Forward' },
    optionTitle: { default: 'Option' },
    totalTitle: { default: 'Aggregate Limit' },
    onBehalfOfClient: { default: null }
  },
  setup(__props: any) {

const props = __props as { instrumentHeader: string, limitHeader: string, inUseHeader: string, remainingHeader: string, spotTitle: string, forwardTitle: string, optionTitle: string, totalTitle: string, onBehalfOfClient: Client | null };



const confirmNotifyUsModal = ref<InstanceType<typeof BModal> | null>(null);

const thankyouNotifyUsModal = ref<InstanceType<typeof BModal> | null>(null);

const tradeState = useTradeState();

const onBehalfOfClient = useOnBehalfOf();

const state = reactive<{
  report: ClientPositionUsageReport | null;
  refreshComponentTimeout: number | null;
}>({
  report: null,
  refreshComponentTimeout: null,
});

const requestManager = useRequestManager({
  exposeToParent: ['loadReport'],
  onRetryFromParentManager: (k: string) => {
    if (k === 'loadReport') {
      loadReport();
    }
  },
});

function loadReport() {
  requestManager.manager
    .currentOrNew(
      'loadReport',
      tradeState.services.risk
        .getClientPositionLimitsUsage(
          onBehalfOfClient.value?.id ?? tradeState.store.useAuthStore().loggedInIdentity!.client!.id,
          {
            errors: { silent: true },
          }
        )
        .pipe(
          catchError((e: HttpError) => {
            try {
              if (e.response?.data.code === GenericErrorCodes.RESOURCE_NOT_FOUND) {
                return of(null);
              }
            } catch (e) {}
            throw e;
          })
        )
    )
    .subscribe((report) => {
      state.report = report;
    });
}

function refreshComponent() {
  state.refreshComponentTimeout = window.setInterval(() => {
    loadReport();
  }, DEFAULT_INACTIVITY_TIMEOUT);
}

onMounted(() => {
  refreshComponent();
});

const canNotifyUs = computed(() =>
  tradeState.store.useAuthStore().hasAuthorities(AuthorityType.NOTIFY_LIMITS_TRADING_DESK)
);

const reportAmounts = computed(() => {
  if (!state.report) return [];
  return [
    {
      instrument: props.spotTitle,
      notionalLimit: state.report.notionalLimitSpots,
      notionalLimitUsage: state.report.notionalLimitSpotsInUse,
      notionalLimitRemaining: Math.max(0, state.report.notionalLimitSpots - state.report.notionalLimitSpotsInUse),
    },
    {
      instrument: props.forwardTitle,
      notionalLimit: state.report.notionalLimitForwards,
      notionalLimitUsage: state.report.notionalLimitForwardsInUse,
      notionalLimitRemaining: Math.max(0, state.report.notionalLimitForwards - state.report.notionalLimitForwardsInUse),
    },
    {
      instrument: props.optionTitle,
      notionalLimit: state.report.notionalLimitOptions,
      notionalLimitUsage: state.report.notionalLimitOptionsInUse,
      notionalLimitRemaining: Math.max(0, state.report.notionalLimitOptions - state.report.notionalLimitOptionsInUse),
    },
    {
      instrument: props.totalTitle,
      notionalLimit: state.report.notionalLimitTotal,
      notionalLimitUsage: state.report.notionalLimitTotalInUse,
      notionalLimitRemaining: Math.max(0, state.report.notionalLimitTotal - state.report.notionalLimitTotalInUse),
    },
  ];
});

onBeforeMount(() => {
  loadReport();
});

function onConfirmNotify() {
  requestManager.manager.currentOrNew('requestNotify', tradeState.services.limits.notifyUsLimit()).subscribe(() => {
    thankyouNotifyUsModal.value?.show();
  });
}

function confirmNotifyUs() {
  confirmNotifyUsModal.value?.show();
}

onBeforeUnmount(() => {
  if (state.refreshComponentTimeout !== null) clearInterval(state.refreshComponentTimeout);
});

return { __sfc: true,props, confirmNotifyUsModal, thankyouNotifyUsModal, tradeState, onBehalfOfClient, state, requestManager, loadReport, refreshComponent, canNotifyUs, reportAmounts, onConfirmNotify, confirmNotifyUs, BModal, formatCurrencyValue }
}

})